// Fixed-timestep game loop with an interpolated render.
//
// The simulation NEVER sees a wall-clock delta — it advances in whole DT steps only, and the
// renderer interpolates between the last two states for smoothness. A free-running
// requestAnimationFrame loop feeding a variable dt into the boids would make every run
// irreproducible and the shared daily leaderboard meaningless.

// TICK_INTERVAL, not DT. This loop schedules against the wall clock, so it needs REAL seconds per
// tick (1/60). DT is WORLD-time per tick and scales with SIM_SPEED — using it here made the sim run
// at 20Hz and the on-screen clock run at a third of real time.
import { TICK_INTERVAL } from './engine/sim'

/** Cap on catch-up steps per frame. Without this, a backgrounded tab returns and freezes on resume. */
const MAX_STEPS_PER_FRAME = 5

export interface LoopHandle {
  stop(): void
}

export function startLoop(
  step: () => void,
  render: (alpha: number) => void,
): LoopHandle {
  let accumulator = 0
  let last = performance.now()
  let raf = 0
  let running = true

  const frame = (now: number) => {
    if (!running) return
    raf = requestAnimationFrame(frame)

    // Seconds since the last frame, clamped so a long stall does not spiral.
    let elapsed = (now - last) / 1000
    last = now
    if (elapsed > 0.25) elapsed = 0.25
    accumulator += elapsed

    let steps = 0
    while (accumulator >= TICK_INTERVAL && steps < MAX_STEPS_PER_FRAME) {
      step()
      accumulator -= TICK_INTERVAL
      steps++
    }
    // Drop any remaining backlog rather than accumulating debt forever.
    if (accumulator > TICK_INTERVAL * MAX_STEPS_PER_FRAME) accumulator = 0

    render(accumulator / TICK_INTERVAL)
  }

  raf = requestAnimationFrame(frame)

  return {
    stop() {
      running = false
      cancelAnimationFrame(raf)
    },
  }
}
